import type { DayId } from '../../domain/calendar.ts'
import {
  DomainEventSchema,
  GENESIS_HASH,
  StoredEventSchema,
  type DomainEvent,
  type StoredEvent,
} from '../../domain/events.ts'
import type { DeviceId, EventId } from '../../domain/ids.ts'
import type { ChainStatus, EventStore, Random } from '../../application/ports.ts'
import { resolveDeviceId } from './db.ts'
import type { MisionHogarDb } from './db.ts'
import { hashEvent } from './hash.ts'

/**
 * Replay order.
 *
 * `seq` orders the events of *one* device and says nothing about two, so across
 * devices the instant decides — that is the order things actually happened in.
 * It compares the instant and not the string: `...T02:30:00+02:00` and
 * `...T02:30:00+01:00` are the same text an hour apart, which is exactly what
 * the night the clocks go back looks like in this log.
 *
 * The device id and then `seq` break a tie, so the order is the same on every
 * tablet. Without that, two events stamped on the same second would project in
 * one order here and the other order there.
 */
function inReplayOrder(a: StoredEvent, b: StoredEvent): number {
  const instantA = Date.parse(a.at)
  const instantB = Date.parse(b.at)
  if (instantA !== instantB) return instantA - instantB
  if (a.deviceId !== b.deviceId) return a.deviceId < b.deviceId ? -1 : 1
  return a.seq - b.seq
}

/**
 * The append-only log, on IndexedDB.
 *
 * This is the only module in the app that writes to the `events` table, and it
 * only ever adds. There is no update and no delete, which is the storage-level
 * expression of the rule the whole house runs on.
 */
export class DexieEventStore implements EventStore {
  private readonly db: MisionHogarDb
  private readonly device: DeviceId
  private readonly random: Random

  private constructor(db: MisionHogarDb, device: DeviceId, random: Random) {
    this.db = db
    this.device = device
    this.random = random
  }

  static async open(db: MisionHogarDb, random: Random): Promise<DexieEventStore> {
    const device = await resolveDeviceId(db, () => random.id())
    return new DexieEventStore(db, device, random)
  }

  deviceId(): DeviceId {
    return this.device
  }

  async append(events: readonly DomainEvent[]): Promise<readonly StoredEvent[]> {
    if (events.length === 0) return []
    for (const event of events) {
      const parsed = DomainEventSchema.safeParse(event)
      if (!parsed.success) {
        throw new TypeError(`Refusing to append a malformed ${event.type}: ${parsed.error.message}`)
      }
    }

    const head = await this.head()
    let seq = head?.seq ?? 0
    let prevHash = head?.hash ?? GENESIS_HASH

    const stamped: StoredEvent[] = []
    for (const event of events) {
      seq += 1
      const stored: StoredEvent = {
        ...event,
        id: this.random.id() as EventId,
        deviceId: this.device,
        seq,
        prevHash,
      }
      stamped.push(stored)
      prevHash = await hashEvent(stored)
    }

    // One transaction: either the whole command lands or none of it does.
    await this.db.transaction('rw', this.db.events, async () => {
      await this.db.events.bulkAdd(stamped)
    })
    return stamped
  }

  /**
   * The whole log, from every device, in replay order.
   *
   * This used to read only `[deviceId+seq]` for *this* device, and that quietly
   * made the backup useless. Restoring a file on a second tablet put the rows in
   * the table and the projection never saw one of them: the app reported the
   * events as added and carried on with the coins at zero, and the next export
   * came out empty because it read the same narrow range. What gets projected
   * and what gets exported has to be the entire table.
   */
  async readAll(): Promise<readonly StoredEvent[]> {
    const rows = await this.db.events.toArray()
    return rows.sort(inReplayOrder)
  }

  async readSince(seq: number): Promise<readonly StoredEvent[]> {
    return this.db.events
      .where('[deviceId+seq]')
      .between([this.device, seq], [this.device, Infinity], false, true)
      .toArray()
  }

  async readDay(day: DayId): Promise<readonly StoredEvent[]> {
    const rows = await this.db.events.where('day').equals(day).toArray()
    return rows.sort(inReplayOrder)
  }

  async count(): Promise<number> {
    return this.db.events.count()
  }

  async head(): Promise<{ seq: number; hash: string } | null> {
    const last = await this.db.events
      .where('[deviceId+seq]')
      .between([this.device, 0], [this.device, Infinity])
      .last()
    if (!last) return null
    return { seq: last.seq, hash: await hashEvent(last) }
  }

  /**
   * Walks the hash chains — one per device, not one per log.
   *
   * `seq` and `prevHash` are stamped on append, so each tablet builds its own
   * chain starting from the genesis hash. Walking a restored log as a single
   * chain would report a gap the moment there are two devices in it, and a
   * warning that cries wolf is a warning nobody reads.
   */
  async verify(): Promise<ChainStatus> {
    const byDevice = new Map<DeviceId, StoredEvent[]>()
    for (const event of await this.readAll()) {
      const chain = byDevice.get(event.deviceId)
      if (chain) chain.push(event)
      else byDevice.set(event.deviceId, [event])
    }

    let checked = 0
    for (const [device, events] of byDevice) {
      const chain = [...events].sort((a, b) => a.seq - b.seq)
      let expectedPrev = GENESIS_HASH
      let expectedSeq = 1
      for (const event of chain) {
        if (event.seq !== expectedSeq) {
          return { ok: false, checked, brokenAt: event.seq, reason: 'gap', onDevice: device }
        }
        if (event.prevHash !== expectedPrev) {
          return {
            ok: false,
            checked,
            brokenAt: event.seq,
            reason: 'hash-mismatch',
            onDevice: device,
          }
        }
        expectedPrev = await hashEvent(event)
        expectedSeq += 1
        checked += 1
      }
    }
    return { ok: true, checked }
  }

  async importEvents(
    events: readonly StoredEvent[],
  ): Promise<{ imported: number; skipped: number }> {
    let imported = 0
    let skipped = 0
    for (const event of events) {
      const parsed = StoredEventSchema.safeParse(event)
      if (!parsed.success) {
        skipped += 1
        continue
      }
      const existing = await this.db.events.get(event.id)
      if (existing) {
        skipped += 1
        continue
      }
      await this.db.events.add(event)
      imported += 1
    }
    return { imported, skipped }
  }
}
